import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { ComplianceDashboard } from '../components/compliance';
import companyService from '../services/companyService';
import documentStorageService from '../services/documentStorageService';

const toDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value.toDate === 'function') {
    try {
      const parsed = value.toDate();
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch (error) {
      return null;
    }
  }

  if (typeof value === 'object') {
    const seconds = typeof value.seconds === 'number' ? value.seconds : value._seconds;
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : value._nanoseconds;

    if (typeof seconds === 'number') {
      const millis = (seconds * 1000) + Math.floor((nanos || 0) / 1_000_000);
      const parsed = new Date(millis);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  return null;
};

const deriveEntityDetails = (doc = {}) => {
  const entityType = doc.driverId ? 'driver' : doc.vehicleId ? 'vehicle' : 'company';
  const entityId = doc.driverId || doc.vehicleId || doc.companyId || null;

  const possibleNames = [
    doc.entityName,
    doc.driverName,
    doc.driverDisplayName,
    doc.driver,
    doc.vehicleName,
    doc.vehicleNumber,
    doc.vehicleDisplayName,
    doc.companyName,
    entityId ? `${entityType.toUpperCase()} ${entityId}` : null,
    'Fleet Operations'
  ];

  const entityName = possibleNames.find((name) => typeof name === 'string' && name.trim().length > 0) || 'Fleet Operations';

  return {
    entityId,
    entityType,
    entityName
  };
};

const COMPLIANCE_TASK_LOOKBACK_DAYS = 60;

const buildTimelineEvents = (documents = [], expiringDocuments = []) => {
  const now = new Date();
  const events = new Map();

  const registerEvent = (event) => {
    if (!event || !event.date) {
      return;
    }

    const date = toDate(event.date);
    if (!date) {
      return;
    }

    const key = `${event.id || event.title}-${date.toISOString()}-${event.type}`;
    events.set(key, {
      ...event,
      date: date.toISOString()
    });
  };

  const appendDocumentEvents = (doc) => {
    const expirationDate = toDate(doc.expirationDate);
    const issueDate = toDate(doc.issueDate);

    if (expirationDate) {
      const daysRemaining = differenceInCalendarDays(expirationDate, now);
      const isExpired = daysRemaining < 0;
      const isUrgent = daysRemaining >= 0 && daysRemaining <= 7;
      const isExpiringSoon = daysRemaining >= 0 && daysRemaining <= 30;

      registerEvent({
        id: `document-${doc.id}-expiration`,
        date: expirationDate,
        type: isExpired ? 'violation' : 'deadline',
        category: doc.documentType || doc.category || 'document',
        title: doc.title || doc.documentType || 'Compliance Document',
        description: isExpired
          ? 'Document is past due and requires immediate attention.'
          : 'Document expiration approaching. Prepare renewal paperwork.',
        entityName: doc.entityName,
        entityType: doc.entityType,
        documentNumber: doc.documentNumber,
        issuingAuthority: doc.issuingAuthority,
        status: isExpired ? 'violation' : 'upcoming',
        priority: isExpired || isUrgent ? 'critical' : isExpiringSoon ? 'high' : 'medium',
        daysRemaining
      });
    }

    if (issueDate) {
      registerEvent({
        id: `document-${doc.id}-issued`,
        date: issueDate,
        type: 'renewal',
        category: doc.documentType || doc.category || 'document',
        title: `${doc.title || 'Compliance Document'} issued`,
        description: 'Document was issued or renewed.',
        entityName: doc.entityName,
        entityType: doc.entityType,
        documentNumber: doc.documentNumber,
        issuingAuthority: doc.issuingAuthority,
        status: 'completed',
        priority: doc.isCritical ? 'critical' : 'medium'
      });
    }
  };

  documents.forEach(appendDocumentEvents);

  expiringDocuments.forEach((doc) => {
    // Expiring list may contain overlapping records; avoid duplicates by using map key
    appendDocumentEvents(doc);
  });

  const createComplianceTaskEvents = (year) => {
    const tasks = [
      {
        key: 'clearinghouse-annual-query',
        month: 0,
        day: 5,
        title: 'Clearinghouse Annual Query',
        description: 'Complete the FMCSA Drug & Alcohol Clearinghouse annual query for each CDL driver.',
        category: 'clearinghouse',
        priority: 'high',
        tags: ['drug & alcohol', 'clearinghouse']
      },
      {
        key: 'ifta-q4-filing',
        month: 0,
        day: 31,
        title: 'IFTA Q4 Fuel Tax Return Due',
        description: 'File and pay the International Fuel Tax Agreement return for Q4 of the previous year.',
        category: 'ifta',
        priority: 'critical',
        tags: ['ifta', 'fuel tax']
      },
      {
        key: 'osha-300a-posting',
        month: 1,
        day: 1,
        title: 'OSHA 300A Summary Posting',
        description: 'Post OSHA 300A summary form for work-related injuries and illnesses (if applicable).',
        category: 'safety',
        priority: 'medium',
        tags: ['osha', 'safety']
      },
      {
        key: 'driver-qualification-review',
        month: 2,
        day: 15,
        title: 'Driver Qualification File Annual Review',
        description: 'Audit driver qualification files, medical certificates, and MVRs for each driver.',
        category: 'driver',
        priority: 'high',
        tags: ['driver', 'dqf']
      },
      {
        key: 'ifta-q1-filing',
        month: 3,
        day: 30,
        title: 'IFTA Q1 Fuel Tax Return Due',
        description: 'File and pay the International Fuel Tax Agreement return for Quarter 1.',
        category: 'ifta',
        priority: 'critical',
        tags: ['ifta', 'fuel tax']
      },
      {
        key: 'mcs-150-biennial-check',
        month: 5,
        day: 30,
        title: 'MCS-150 Biennial Update Check',
        description: 'Confirm USDOT Biennial Update (MCS-150) due month aligns with DOT number; file if scheduled this year.',
        category: 'dot',
        priority: 'high',
        tags: ['dot', 'mcs-150']
      },
      {
        key: 'ifta-q2-filing',
        month: 6,
        day: 31,
        title: 'IFTA Q2 Fuel Tax Return Due',
        description: 'File and pay the International Fuel Tax Agreement return for Quarter 2.',
        category: 'ifta',
        priority: 'critical',
        tags: ['ifta', 'fuel tax']
      },
      {
        key: 'hvut-form-2290',
        month: 7,
        day: 31,
        title: 'HVUT Form 2290 Filing Deadline',
        description: 'File IRS Form 2290 Heavy Vehicle Use Tax for vehicles 55,000 lbs and above.',
        category: 'tax',
        priority: 'critical',
        tags: ['irs', 'hvut', 'tax']
      },
      {
        key: 'ifta-q3-filing',
        month: 9,
        day: 31,
        title: 'IFTA Q3 Fuel Tax Return Due',
        description: 'File and pay the International Fuel Tax Agreement return for Quarter 3.',
        category: 'ifta',
        priority: 'critical',
        tags: ['ifta', 'fuel tax']
      },
      {
        key: 'irp-apportioned-renewal',
        month: 10,
        day: 15,
        title: 'IRP Apportioned Plate Renewal Planning',
        description: 'Gather mileage and vehicle data for IRP apportioned plate renewals.',
        category: 'irp',
        priority: 'high',
        tags: ['irp', 'registration']
      },
      {
        key: 'insurance-renewal-review',
        month: 10,
        day: 30,
        title: 'Insurance Renewal Review',
        description: 'Review fleet insurance policies, certificates, and coverage before renewal.',
        category: 'insurance',
        priority: 'high',
        tags: ['insurance']
      },
      {
        key: 'ifta-license-renewal',
        month: 11,
        day: 15,
        title: 'IFTA & IRP License Renewals',
        description: 'Renew IFTA credentials and update cab cards, decals, and IRP accounts for the upcoming year.',
        category: 'ifta',
        priority: 'high',
        tags: ['ifta', 'irp', 'renewal']
      },
      {
        key: 'ucr-registration',
        month: 11,
        day: 31,
        title: 'Unified Carrier Registration (UCR)',
        description: 'Complete annual UCR registration and fee payment for the upcoming year.',
        category: 'ucr',
        priority: 'high',
        tags: ['ucr', 'registration']
      }
    ];

    tasks.forEach((task) => {
      const taskDate = task.getDate ? task.getDate(year) : new Date(year, task.month, task.day);

      if (!(taskDate instanceof Date) || Number.isNaN(taskDate.getTime())) {
        return;
      }

      const daysRemaining = differenceInCalendarDays(taskDate, now);
      let status = 'upcoming';

      if (daysRemaining < -COMPLIANCE_TASK_LOOKBACK_DAYS) {
        return;
      }

      if (daysRemaining < 0) {
        status = 'completed';
      } else if (daysRemaining <= 30) {
        status = 'warning';
      }

      const detailsKey = `compliance-${task.key}`;

      registerEvent({
        id: `${detailsKey}-${year}`,
        detailsKey,
        complianceKey: detailsKey,
        date: taskDate,
        type: 'compliance',
        category: task.category,
        title: task.title,
        description: task.description,
        entityName: 'Fleet Operations',
        entityType: 'company',
        status,
        priority: task.priority || (daysRemaining <= 30 ? 'high' : 'medium'),
        daysRemaining,
        tags: task.tags || []
      });
    });
  };

  const currentYear = now.getFullYear();
  [currentYear - 1, currentYear, currentYear + 1].forEach((year) => {
    if (year >= 2000 && year <= currentYear + 2) {
      createComplianceTaskEvents(year);
    }
  });

  return Array.from(events.values()).sort((a, b) => {
    const left = new Date(a.date).getTime();
    const right = new Date(b.date).getTime();
    return left - right;
  });
};

const triggerFileDownload = (response, fallbackName = 'document') => {
  if (typeof window === 'undefined' || !response) {
    return;
  }

  const { data, headers } = response;
  const mimeType = headers?.['content-type'] || 'application/octet-stream';
  const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType });
  const downloadUrl = window.URL.createObjectURL(blob);

  const headerValue = headers?.['content-disposition'] || '';
  const filenameMatch = headerValue.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  const encodedName = filenameMatch?.[1] || filenameMatch?.[2];
  const filename = encodedName ? decodeURIComponent(encodedName) : fallbackName;

  const link = window.document.createElement('a');
  link.href = downloadUrl;
  link.download = filename || 'document';
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const Compliance = () => {
  const [documents, setDocuments] = useState([]);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [complianceScore, setComplianceScore] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFeedback, setActionFeedback] = useState(null);

  const transformDocument = useCallback((doc) => {
    if (!doc || typeof doc !== 'object') {
      return null;
    }

    const expirationDate = toDate(doc.expirationDate);
    const issueDate = toDate(doc.issueDate);
    const updatedAt = toDate(doc.updatedAt);
    const createdAt = toDate(doc.createdAt);
    const daysUntilExpiration = expirationDate ? differenceInCalendarDays(expirationDate, new Date()) : null;

    const computedStatus = (() => {
      if (typeof doc.status === 'string' && doc.status.length > 0) {
        return doc.status;
      }
      if (typeof daysUntilExpiration === 'number') {
        if (daysUntilExpiration < 0) {
          return 'expired';
        }
        if (daysUntilExpiration <= 30) {
          return 'expiring_soon';
        }
      }
      return 'active';
    })();

    const urgency = (() => {
      if (typeof daysUntilExpiration !== 'number') {
        return 'low';
      }
      if (daysUntilExpiration < 0) {
        return 'critical';
      }
      if (daysUntilExpiration <= 7) {
        return 'critical';
      }
      if (daysUntilExpiration <= 30) {
        return 'high';
      }
      if (daysUntilExpiration <= 60) {
        return 'medium';
      }
      return 'low';
    })();

    const { entityId, entityType, entityName } = deriveEntityDetails(doc);

    return {
      ...doc,
      id: doc.id || doc.documentId,
      entityId,
      entityType,
      entityName,
      expirationDate: expirationDate ? expirationDate.toISOString() : null,
      issueDate: issueDate ? issueDate.toISOString() : null,
      updatedAt: updatedAt ? updatedAt.toISOString() : null,
      createdAt: createdAt ? createdAt.toISOString() : null,
      daysUntilExpiration,
      computedStatus,
      urgency
    };
  }, []);

  const fetchComplianceData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [scoreResult, documentsResult, expiringResult] = await Promise.allSettled([
        companyService.getComplianceScore(),
        documentStorageService.getDocuments({}, { params: { limit: 100, offset: 0 } }),
        documentStorageService.getExpiringDocuments(60)
      ]);

      if (scoreResult.status === 'fulfilled') {
        setComplianceScore(scoreResult.value?.complianceScore || null);
      } else {
        console.error('Failed to load compliance score:', scoreResult.reason);
      }

      let normalizedDocuments = [];
      if (documentsResult.status === 'fulfilled') {
        const docs = Array.isArray(documentsResult.value?.documents)
          ? documentsResult.value.documents
          : [];
        normalizedDocuments = docs
          .map(transformDocument)
          .filter(Boolean);
        setDocuments(normalizedDocuments);
      } else {
        console.error('Failed to load documents:', documentsResult.reason);
        setDocuments([]);
      }

      let normalizedExpiring = [];
      if (expiringResult.status === 'fulfilled') {
        const expiringDocs = Array.isArray(expiringResult.value?.documents)
          ? expiringResult.value.documents
          : [];
        normalizedExpiring = expiringDocs
          .map(transformDocument)
          .filter(Boolean);
        setExpiringDocuments(normalizedExpiring);
      } else {
        console.error('Failed to load expiring documents:', expiringResult.reason);
        setExpiringDocuments([]);
      }

      setTimelineEvents(buildTimelineEvents(normalizedDocuments, normalizedExpiring));

      if (
        scoreResult.status === 'rejected' &&
        documentsResult.status === 'rejected' &&
        expiringResult.status === 'rejected'
      ) {
        throw documentsResult.reason || scoreResult.reason || expiringResult.reason || new Error('Failed to load compliance data');
      }
    } catch (apiError) {
      console.error('Compliance data load failed:', apiError);
      setError(apiError?.message || 'Failed to load compliance data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [transformDocument]);

  useEffect(() => {
    fetchComplianceData();
  }, [fetchComplianceData]);

  useEffect(() => {
    if (!actionFeedback) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setActionFeedback(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [actionFeedback]);

  const handleDocumentAction = useCallback(async (action, selectedDocument) => {
    if (!selectedDocument || !selectedDocument.id) {
      setActionFeedback({
        type: 'error',
        message: 'Document reference was not found. Please refresh and try again.'
      });
      return;
    }

    try {
      switch (action) {
        case 'view': {
          if (typeof window !== 'undefined') {
            if (selectedDocument.fileUrl) {
              window.open(selectedDocument.fileUrl, '_blank', 'noopener');
            } else {
              const response = await documentStorageService.getDocumentById(selectedDocument.id);
              const url = response?.document?.fileUrl || response?.fileUrl;
              if (url) {
                window.open(url, '_blank', 'noopener');
              } else {
                throw new Error('Preview is not available for this document.');
              }
            }
          }
          break;
        }
        case 'download': {
          const downloadResponse = await documentStorageService.downloadDocument(selectedDocument.id);
          triggerFileDownload(
            downloadResponse,
            selectedDocument.fileName || `${selectedDocument.title || 'document'}.pdf`
          );
          setActionFeedback({ type: 'success', message: 'Download started.' });
          break;
        }
        case 'renew': {
          await documentStorageService.updateDocument(selectedDocument.id, {
            status: 'pending_renewal',
            renewalNotes: 'Renewal initiated from compliance dashboard',
            renewalRequestedAt: new Date().toISOString()
          });
          setActionFeedback({ type: 'success', message: 'Document flagged for renewal.' });
          await fetchComplianceData();
          break;
        }
        default:
          break;
      }
    } catch (actionError) {
      console.error('Document action failed:', actionError);
      setActionFeedback({
        type: 'error',
        message: actionError?.message || 'Action failed. Please try again.'
      });
    }
  }, [fetchComplianceData]);

  const handleUpload = useCallback((result) => {
    if (result && result.success) {
      fetchComplianceData();
      setActionFeedback({ type: 'success', message: 'Documents uploaded successfully.' });
    }
  }, [fetchComplianceData]);

  const dashboardError = useMemo(() => {
    if (error) {
      return error;
    }
    if (actionFeedback?.type === 'error') {
      return actionFeedback.message;
    }
    return null;
  }, [error, actionFeedback]);

  const dashboardStatus = useMemo(() => {
    if (actionFeedback?.type === 'success') {
      return actionFeedback.message;
    }
    return null;
  }, [actionFeedback]);

  return (
    <ComplianceDashboard
      documents={documents}
      complianceScore={complianceScore}
      expiringDocuments={expiringDocuments}
      timelineEvents={timelineEvents}
      isLoading={isLoading}
      error={dashboardError}
      statusMessage={dashboardStatus}
      onDocumentAction={handleDocumentAction}
      onUpload={handleUpload}
      onRenewal={null}
      onRefresh={fetchComplianceData}
    />
  );
};

export default Compliance;
  const complianceTaskDetails = {
    'compliance-clearinghouse-annual-query': {
      overview: 'The FMCSA Drug & Alcohol Clearinghouse requires employers to run an annual query on every CDL driver under employment to ensure no unresolved drug or alcohol violations.',
      importance: 'Verifies the driver is eligible to operate a commercial motor vehicle and documents compliance with 49 CFR Part 382 requirements.',
      filing: 'Log into the FMCSA Clearinghouse portal (https://clearinghouse.fmcsa.dot.gov), obtain driver consent, and run the annual limited (or full) query. Maintain records for each driver query.'
    },
    'compliance-ifta-q4-filing': {
      overview: 'International Fuel Tax Agreement (IFTA) quarterly return covering October–December fuel purchasing and mileage reporting.',
      importance: 'Ensures fuel taxes are allocated to the jurisdictions in which the fleet operated, preventing penalties or license suspension.',
      filing: 'Submit the IFTA return and payment through your base jurisdiction’s online IFTA portal or mailed form by the due date (typically January 31).' 
    },
    'compliance-osha-300a-posting': {
      overview: 'OSHA requires companies with 10+ employees in most industries to summarize and post workplace injury and illness data (Form 300A) annually.',
      importance: 'Keeps workers informed of safety performance and fulfills OSHA recordkeeping compliance.',
      filing: 'Post Form 300A from February 1 through April 30 in a visible location and, if required, submit electronically through OSHA’s Injury Tracking Application.'
    },
    'compliance-driver-qualification-review': {
      overview: 'Annual review of each driver’s qualification file, including motor vehicle records, medical certificates, and safety performance history.',
      importance: 'Confirms driver fitness under 49 CFR 391 and protects against violations discovered during audits.',
      filing: 'Collect and review updated MVRs, medical cards, and supporting documents; document the annual review in the driver qualification file.'
    },
    'compliance-ifta-q1-filing': {
      overview: 'IFTA quarterly return covering January–March travel and fuel purchases.',
      importance: 'Required to continue displaying valid IFTA decals and avoid assessments.',
      filing: 'Submit through your base state’s IFTA portal or mailed form by April 30 (exact deadlines vary by jurisdiction).'
    },
    'compliance-mcs-150-biennial-check': {
      overview: 'Biennial update of the USDOT number (Form MCS-150) ensures FMCSA has current company and fleet data.',
      importance: 'Failure to update can lead to USDOT number deactivation and penalties.',
      filing: 'Submit Form MCS-150 at https://www.fmcsa.dot.gov/registration/updating-your-registration; due in the month associated with the USDOT number and every two years thereafter.'
    },
    'compliance-ifta-q2-filing': {
      overview: 'IFTA quarterly return covering April–June travel and fuel purchases.',
      importance: 'Maintains good standing with IFTA participation and prevents audit flags.',
      filing: 'Submit online or by mail to your base state typically by July 31.'
    },
    'compliance-hvut-form-2290': {
      overview: 'IRS Heavy Vehicle Use Tax (Form 2290) filing for vehicles 55,000 lbs or more for the period July 1 to June 30.',
      importance: 'Proof of payment is needed to register vehicles; late filings incur penalties and interest.',
      filing: 'File electronically at https://www.irs.gov/truckers or through an approved e-file provider by August 31; obtain Schedule 1 as proof of payment.'
    },
    'compliance-ifta-q3-filing': {
      overview: 'IFTA quarterly return covering July–September travel and fuel purchases.',
      importance: 'Ensures continued compliance with IFTA and avoids assessment of penalties.',
      filing: 'Submit through your base jurisdiction by October 31 (jurisdiction deadlines may vary).'
    },
    'compliance-irp-apportioned-renewal': {
      overview: 'Preparation for renewing International Registration Plan (IRP) apportioned plates, including compiling mileage and vehicle changes.',
      importance: 'Timely renewal avoids vehicle registration lapses and roadside violations.',
      filing: 'Gather mileage data for the reporting period and submit renewal through your state IRP office or online portal (timeline varies by jurisdiction).' 
    },
    'compliance-insurance-renewal-review': {
      overview: 'Annual review of fleet insurance coverage, limits, and certificates prior to policy renewal.',
      importance: 'Ensures sufficient liability and cargo coverage aligning with FMCSA requirements and shipper demands.',
      filing: 'Coordinate with your insurance broker/carrier, verify driver and equipment schedules, and issue updated COIs to stakeholders.'
    },
    'compliance-ifta-license-renewal': {
      overview: 'Renewal of IFTA credentials (licenses, decals, cab cards) for the upcoming year; often aligned with IRP renewals.',
      importance: 'Allows continued interstate operations without purchasing individual trip permits.',
      filing: 'Submit renewal application and fees via your base jurisdiction’s IFTA portal, typically due by December 31.'
    },
    'compliance-ucr-registration': {
      overview: 'Unified Carrier Registration (UCR) annual filing for carriers operating in interstate commerce.',
      importance: 'Federal requirement under 49 USC 14504a; missing registration can lead to enforcement action and fines.',
      filing: 'Complete at https://www.ucr.gov/ for the upcoming year, usually open from October with a January deadline.'
    }
  };
